import { createSlice } from '@reduxjs/toolkit'
import {
  grhType,
  micromobilityType,
  flexType,
  qtfType,
  transitPassType,
  couponType,
  reportedUsageType,
} from '../Shared/types'
import GRHUtils from 'features/GRH/Shared/GRHUtils'
import MicromobilityUtils from 'features/Micromobility/Shared/MicromobilityUtils'
import FlexUtils from 'features/Flex/Shared/FlexUtils'

export interface ProgramEditingState {
  grh: grhType
  micromobility: micromobilityType
  flex: flexType
  transitPass: transitPassType
  coupon: couponType
  reportedUsage: reportedUsageType
  qtf: qtfType
}

export const GRH_INITIAL_STATE: grhType = {
  transaction: GRHUtils.recTransacLimit,
  uses: GRHUtils.recUses,
  activeOptions: {
    rideshare: true,
    localTaxi: true,
    governmentGrh: true,
  },
  activeMerchantIds: {
    rideshare: [],
    localTaxi: [],
  },
  governmentGrhPrograms: [],
}

export const MICROMOBILITY_INITIAL_STATE: micromobilityType = {
  budget: MicromobilityUtils.recBudget,
  prorated: false,
  activeOptions: {
    leasing: true,
    shared: true,
    oneTime: true,
  },
  activeMerchantIds: {
    leasing: [],
    shared: [],
    oneTime: [],
  },
}

export const FLEX_INITIAL_STATE: flexType = {
  budget: FlexUtils.recBudget,
  budgetCycle: 'MONTHLY',
  oneTimePurchase: false,
  rolloverBudget: false,
  prorated: false,
  activeOptions: {
    flex: true,
    gas: true,
    transit: true,
    parking: true,
    tolls: false,
    rideshare: false,
    carshare: false,
    transportationServices: false,
  },
  activeMerchantIds: {
    flex: [],
    gas: [],
    transit: [],
    parking: [],
    tolls: [],
    rideshare: [],
    carshare: [],
    transportationServices: [],
  },
}

export const TRANSIT_PASS_INITIAL_STATE: transitPassType = {
  purchaseType: 'EMPLOYEE_CHOICE',
  walletTopUpEnabled: true,
  budget: 10000,
  passDeliveryMechanism: 'PHYSICAL',
  agenciesEnabled: true,
  transitAgencyIds: [],
  availableTransitAgencies: [],
}

export const COUPON_INITIAL_STATE: couponType = {
  merchantsEnabled: true,
  merchantIds: [],
  availableMerchants: [],
  couponCode: '',
}

export const QTF_INITIAL_STATE: qtfType = {
  payrollSchedule: 'BI_MONTHLY',
  benefits: {
    transit: true,
    parking: true,
  },
  employerContributions: {
    transit: 0,
    parking: 0,
  },
  finalizeAllocation: 27,
  maxAllocationLimit: {
    transit: null,
    parking: null,
  },
}

export const REPORTED_USAGE_INITIAL_STATE: reportedUsageType = {
  merchants: [],
}

const INITIAL_STATE: ProgramEditingState = {
  grh: GRH_INITIAL_STATE,
  micromobility: MICROMOBILITY_INITIAL_STATE,
  flex: FLEX_INITIAL_STATE,
  transitPass: TRANSIT_PASS_INITIAL_STATE,
  coupon: COUPON_INITIAL_STATE,
  reportedUsage: REPORTED_USAGE_INITIAL_STATE,
  qtf: QTF_INITIAL_STATE,
}

const programEditingSlice = createSlice({
  name: 'programEditing',
  initialState: INITIAL_STATE,
  reducers: {
    setGrhTransactionUses: (state, action) => {
      const type = action.payload.type
      state.grh[type] = action.payload.value
    },
    setGrhActiveOptions: (state, action) => {
      const type = action.payload.type
      state.grh.activeOptions[type] = action.payload.value
    },
    setGrhActiveMerchantIds: (state, action) => {
      const type = action.payload.type
      state.grh.activeMerchantIds[type] = action.payload.value
    },
    setMicromobilityBudget: (state, action) => {
      state.micromobility.budget = action.payload
    },
    setMicromobilityProrated: (state, action) => {
      state.micromobility.prorated = action.payload
    },
    setMicromobilityActiveOptions: (state, action) => {
      const type = action.payload.type
      state.micromobility.activeOptions[type] = action.payload.value
    },
    setMicromobilityActiveMerchantIds: (state, action) => {
      const type = action.payload.type
      state.micromobility.activeMerchantIds[type] = action.payload.value
    },
    setFlexBudget: (state, action) => {
      state.flex.budget = action.payload
    },
    setFlexBudgetCycle: (state, action) => {
      state.flex.budgetCycle = action.payload
    },
    setFlexOneTimePurchase: (state, action) => {
      state.flex.oneTimePurchase = action.payload
    },
    setFlexRolloverBudget: (state, action) => {
      state.flex.rolloverBudget = action.payload
    },
    setFlexProrated: (state, action) => {
      state.flex.prorated = action.payload
    },
    setFlexActiveOptions: (state, action) => {
      const type = action.payload.type
      state.flex.activeOptions[type] = action.payload.value
    },
    setFlexActiveMerchantIds: (state, action) => {
      const type = action.payload.type
      state.flex.activeMerchantIds[type] = action.payload.value
    },
    setTransitPassPurchaseType: (state, action) => {
      state.transitPass.purchaseType = action.payload
    },
    setTransitPassWalletTopUpEnabled: (state, action) => {
      state.transitPass.walletTopUpEnabled = action.payload
    },
    setTransitPassBudget: (state, action) => {
      state.transitPass.budget = action.payload
    },
    setTransitPassDeliveryMechanism: (state, action) => {
      state.transitPass.passDeliveryMechanism = action.payload
    },
    setTransitPassAgencyIds: (state, action) => {
      state.transitPass.transitAgencyIds = action.payload
    },
    setTransitPassAgenciesEnabled: (state, action) => {
      state.transitPass.agenciesEnabled = action.payload
      if (!action.payload) {
        state.transitPass.transitAgencyIds = []
      } else if (
        state.transitPass.transitAgencyIds.length === 0 &&
        state.transitPass.availableTransitAgencies.length > 0
      ) {
        state.transitPass.transitAgencyIds =
          state.transitPass.availableTransitAgencies.map((agency) => agency.id)
      }
    },
    setTransitPassAvailableAgencies: (state, action) => {
      state.transitPass.availableTransitAgencies = action.payload
      if (
        state.transitPass.agenciesEnabled &&
        state.transitPass.transitAgencyIds.length === 0 &&
        action.payload.length > 0
      ) {
        state.transitPass.transitAgencyIds = action.payload.map(
          (agency: { id: string }) => agency.id
        )
      }
    },
    setCouponMerchantIds: (state, action) => {
      state.coupon.merchantIds = action.payload.slice(0, 1)
      if (state.coupon.merchantIds.length === 0) {
        state.coupon.couponCode = ''
      }
    },
    setCouponMerchantsEnabled: (state, action) => {
      state.coupon.merchantsEnabled = action.payload
      if (!action.payload) {
        state.coupon.merchantIds = []
        state.coupon.couponCode = ''
      }
    },
    setCouponAvailableMerchants: (state, action) => {
      state.coupon.availableMerchants = action.payload
    },
    setCouponCode: (state, action) => {
      state.coupon.couponCode = action.payload
    },
    setReportedUsageMerchants: (state, action) => {
      state.reportedUsage.merchants = action.payload
    },
    setQtfBenefits: (state, action) => {
      const type = action.payload.type
      state.qtf.benefits[type] = action.payload.value
    },
    setQtfEmployerContributions: (state, action) => {
      const type = action.payload.type
      state.qtf.employerContributions[type] = action.payload.value
    },
    setQtfFinalizeAllocation: (state, action) => {
      state.qtf.finalizeAllocation = action.payload
    },
    setQtfPayrollSchedule: (state, action) => {
      state.qtf.payrollSchedule = action.payload
    },
    setQtfMaxAllocationLimit: (state, action) => {
      const type = action.payload.type
      state.qtf.maxAllocationLimit[type] = action.payload.value
    },
    rehydrateState: (state, action) => {
      state.grh = action.payload.grh
      state.micromobility = action.payload.micromobility
      state.flex = action.payload.flex
      state.transitPass = {
        ...TRANSIT_PASS_INITIAL_STATE,
        ...(action.payload.transitPass || {}),
        availableTransitAgencies:
          action.payload.transitPass?.availableTransitAgencies ||
          state.transitPass.availableTransitAgencies ||
          [],
      }
      state.coupon = {
        ...COUPON_INITIAL_STATE,
        ...(action.payload.coupon || {}),
        availableMerchants:
          action.payload.coupon?.availableMerchants ||
          state.coupon.availableMerchants ||
          [],
      }
      state.reportedUsage = {
        ...REPORTED_USAGE_INITIAL_STATE,
        ...(action.payload.reportedUsage || {}),
      }
      state.qtf = action.payload.qtf
    },
    setInitialState: (state) => {
      return INITIAL_STATE
    },
    setGovernmentGrhPrograms: (state, action) => {
      state.grh.governmentGrhPrograms = action.payload
    },
  },
})

export const programEditingReducer = programEditingSlice.reducer
export const programEditingReducers = {
  ...programEditingSlice.actions,
}
